import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly } from '@ember/object/computed';
import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import NewsletterModel from 'cio/models/newsletter';
import ExportsService from 'cio/services/exports';
import { Metric } from 'cio/utils/metrics/models/metric';
import TemplateModel from 'cio/models/template';
import { tracked } from '@glimmer/tracking';
import MetricsService from 'cio/services/metrics';
import { ActionType } from 'cio/models/action';

export default class NewsletterOverviewTestController extends Controller {
  declare model: NewsletterModel;

  @service exports!: ExportsService;
  @service metrics!: MetricsService;

  @readOnly('model') newsletter!: NewsletterModel;

  @tracked templateMetrics: Metric[] | null = null;

  isSendingToRemaining = false;
  winnerTemplate: TemplateModel | null = null;

  fetchMetrics = task({ keepLatest: true }, async () => {
    let actionType = `${this.model.channel}_action` as ActionType;
    this.templateMetrics = await this.metrics.fetchTemplateMetrics(this.model.id, actionType);
  });

  sendNewsletter = task(
    { keepLatest: true },
    async ({
      timestamp,
      timezone,
      winnerTemplate,
    }: {
      timestamp: number;
      timezone: string;
      winnerTemplate: TemplateModel;
    }) => {
      let { newsletter } = this;

      if (newsletter.sentToRemaining) {
        return;
      }

      // We are handling a few different cases here:
      //
      // - if the test is running:
      //  - if we want to end it (right now or schedule), then we send an `end_test` update type
      //
      // - if the test is ended:
      //  - if we want to reschedule, we send a `schedule` update type
      //  - if we want to remove any existing schedule and send immediately, we send a `send` update type.

      let updateType;

      if (newsletter.testEnded) {
        updateType = timestamp && timezone ? 'schedule' : 'send';
      } else {
        updateType = 'end_test';
      }

      newsletter.setProperties({
        timezone,
        winnerTemplate,
        scheduledAt: timestamp,
        updateType,
      });

      try {
        await newsletter.save();
        this.setDefaults();
      } catch (errors) {
        newsletter.rollbackAttributes();
      }
    },
  );

  setDefaults() {
    this.setProperties({
      winnerTemplate: null,
      isSendingToRemaining: false,
    });
  }

  @action
  createExport() {
    return this.exports.deliveryTotals({ newsletterId: this.newsletter.id, from: 'newsletter_test' });
  }

  @action
  toggleSendToRemainingModal(winnerTemplate: TemplateModel) {
    this.set('winnerTemplate', winnerTemplate);
    this.toggleProperty('isSendingToRemaining');
  }
}

declare module '@ember/controller' {
  interface Registry {
    'broadcasts.newsletter.overview.test': NewsletterOverviewTestController;
  }
}
